import {useMemo, useState} from "react";
import {
    Grid, Button, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography, TextField, Autocomplete,
} from "@mui/material";
import {Controller} from "react-hook-form";
import {useTranslation} from "react-i18next";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useGuardianAddressForm} from "../hooks/useGuardianAddressForm";
import {TITLES_OPTIONS} from "../../../constants/titles";
import {useGetRelationships} from "../../../hooks/useGetRelationships";
import Preloader from "../../../components/Preloader";
import {useApplicationStorage} from "../../../hooks/useApplicationStorage";
import {useGetCountries} from "../../../hooks/useGetCountries";
import {useAddressFunctions, highlightMatch} from "../../../hooks/useAddressFunctions";

export const GuardianAddressForm = (props) => {
    const {data, onFinish, isSaving} = props;
    const {t} = useTranslation();
    const {leadData} = useApplicationStorage();

    const {isLoading: isLoadingRelationships, relationshipsOptions} = useGetRelationships();
    const {isLoading: isLoadingCountries, countriesOptions} = useGetCountries();

    const [showAddressForm, setShowAddressForm] = useState(data?.hasOtherAddress);

    const {
        handleSubmit, control, errors, watch, setValue, isDirty,
    } = useGuardianAddressForm(data, showAddressForm);
    const relationValue = watch("relationshipTypeId");
    const country = watch("country");

    const {
        suggestions,
        isSearching,
        searchQuery,
        setSearchQuery,
        open,
        setOpen,
        autocompleteRef,
        handleAddressSearch,
        handleAddressSelect,
    } = useAddressFunctions(country, setValue);

    const handleChangeAddress = () => {
        setShowAddressForm(true);
        setValue("country", "");
        setValue("addressLine1", "");
        setValue("addressLine2", "");
        setValue("city", "");
        setValue("code", "");
        setValue("area", "");
    };

    const handleCancelChangeAddress = () => {
        setShowAddressForm(false);
        setValue("country", leadData?.country);
        setValue("addressLine1", leadData?.addressLine1);
        setValue("addressLine2", leadData?.addressLine2);
        setValue("city", leadData?.city);
        setValue("code", leadData?.code);
        setValue("area", leadData?.area);
    };

    const formattedParentAddress = useMemo(() => {
        const country = countriesOptions.find((opt) => opt.value === leadData.country)?.label || "";
        return `${leadData.addressLine1}${leadData.addressLine2 ? `, ${leadData.addressLine2}` : ""}, ${leadData.area}, ${leadData.city}, ${leadData.code}, ${country}`;
    }, [leadData, countriesOptions]);

    const onSubmit = (dataForm) => {
        const hasGuardian =
            dataForm.responsibleGuardianTitle ||
            dataForm.responsibleGuardianForename ||
            dataForm.responsibleGuardianSurname ||
            dataForm.responsibleGuardianEmail ||
            dataForm.responsibleGuardianPhone ||
            dataForm.responsibleGuardianRelationshipId;

        const hasAddress =
            showAddressForm &&
            (dataForm.addressLine1 ||
                dataForm.addressLine2 ||
                dataForm.city ||
                dataForm.area ||
                dataForm.code ||
                dataForm.country);

        const request = {
            relationshipTypeId: dataForm.relationshipTypeId,
            responsibleGuardian: hasGuardian
                ? {
                    title: dataForm.responsibleGuardianTitle,
                    forename: dataForm.responsibleGuardianForename,
                    surname: dataForm.responsibleGuardianSurname,
                    email: dataForm.responsibleGuardianEmail,
                    phone: dataForm.responsibleGuardianPhone,
                    relationshipTypeId: dataForm.responsibleGuardianRelationshipId,
                }
                : null,
            address: {
                addressLine1: hasAddress ? dataForm.addressLine1 : leadData?.addressLine1,
                addressLine2: hasAddress ? dataForm.addressLine2 : leadData?.addressLine2,
                city: hasAddress ? dataForm.city : leadData?.city,
                area: hasAddress ? dataForm.area : leadData?.area,
                code: hasAddress ? dataForm.code : leadData?.code,
                country: hasAddress ? dataForm.country : leadData?.country,
            },
        };
        onFinish?.({...request, hasOtherAddress: showAddressForm}, isDirty);
    };

    return (
        <Preloader isLoading={isLoadingRelationships || isLoadingCountries} overflow>
            <form className="Students-form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    <Grid item size={12}>
                        <FormControl fullWidth error={!!errors.relationshipTypeId}>
                            <InputLabel required>{t("students.address.relation")}</InputLabel>
                            <Controller
                                name="relationshipTypeId"
                                control={control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        onChange={(event) => {
                                            field.onChange(event.target.value);
                                            handleCancelChangeAddress();
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em className="select-placeholder">{t("students.address.placeholders.relation")}</em>
                                        </MenuItem>
                                        {relationshipsOptions.map((item) => (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.relationshipTypeId?.message ? (
                                <FormHelperText>{errors.relationshipTypeId.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    {(relationValue && !showAddressForm) && (
                        <>
                            <Grid item size={12}>
                                <Typography variant="h2">{t("students.address.studentAddressTitle")}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <Typography variant="body1">{formattedParentAddress}</Typography>
                                <Button
                                    variant="text"
                                    className="button_underlined"
                                    onClick={handleChangeAddress}
                                >
                                    {t("students.address.changeAddress")}
                                </Button>
                            </Grid>
                        </>
                    )}
                    {(relationValue && showAddressForm) && (
                        <>
                            <Grid item size={12}>
                                <Typography variant="h2">{t("students.address.studentAddressTitle")}</Typography>
                            </Grid>
                            <Grid item size={12}>
                                <FormControl fullWidth error={!!errors.country}>
                                    <InputLabel required>{t("students.address.country")}</InputLabel>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({field}) => (
                                            <Autocomplete
                                                {...field}
                                                value={countriesOptions.find((opt) => opt.value === field.value) || null}
                                                onChange={(event, newValue) => {
                                                    field.onChange(newValue?.value || null);
                                                }}
                                                disablePortal
                                                options={countriesOptions}
                                                renderInput={(params) => <TextField {...params}
                                                    placeholder={t("students.address.placeholders.country")}/>}
                                            />
                                        )}
                                    />
                                    {errors.country?.message ? (
                                        <FormHelperText>{errors.country.message}</FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item size={12}>
                                <Controller
                                    name="addressLine1"
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                        <Autocomplete
                                            ref={autocompleteRef}
                                            freeSolo
                                            open={open}
                                            onOpen={() => {
                                                if (suggestions.length > 0) setOpen(true);
                                            }}
                                            onClose={(_, reason) => {
                                                if (reason === "blur" || reason === "escape") setOpen(false);
                                            }}
                                            options={suggestions}
                                            loading={isSearching}
                                            getOptionLabel={(option) =>
                                                typeof option === "string" ? option : option.text || ""
                                            }
                                            filterOptions={(x) => x}
                                            onInputChange={(_, value, reason) => {
                                                if (reason === "input") {
                                                    field.onChange(value);
                                                    setSearchQuery(value);
                                                    handleAddressSearch(value);
                                                }
                                            }}
                                            onChange={(_, newValue) => {
                                                if (newValue && typeof newValue !== "string") {
                                                    handleAddressSelect(newValue);
                                                }
                                            }}
                                            inputValue={field.value || ""}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    slotProps={{
                                                        ...params.slotProps,
                                                        htmlInput: {...params.inputProps, required: false},
                                                    }}
                                                    label={t("students.address.addressLine1")}
                                                    placeholder={t("students.address.placeholders.addressLine1")}
                                                    required
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    onBlur={field.onBlur}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <div className="w-full flex align-center">
                                                        <span>{highlightMatch(option.text, searchQuery)}</span>
                                                        {option.description && (
                                                            <span style={{color: "gray", marginLeft: 8}}>
                                                                {option.description}
                                                            </span>
                                                        )}
                                                        {option.type === "Container" && (
                                                            <ChevronRightIcon style={{marginLeft: "auto"}}/>
                                                        )}
                                                    </div>
                                                </li>
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Controller
                                    name="addressLine2"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label={t("students.address.addressLine2")}
                                            placeholder={t("students.address.placeholders.addressLine2")}
                                            error={!!errors.addressLine2}
                                            helperText={errors.addressLine2?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Controller
                                    name="code"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            slotProps={{htmlInput: {required: false}}}
                                            label={t("students.address.zipCode")}
                                            placeholder={t("students.address.placeholders.zipCode")}
                                            required
                                            error={!!errors.code}
                                            helperText={errors.code?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            slotProps={{htmlInput: {required: false}}}
                                            label={t("students.address.city")}
                                            placeholder={t("students.address.placeholders.city")}
                                            required
                                            error={!!errors.city}
                                            helperText={errors.city?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Controller
                                    name="area"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            slotProps={{htmlInput: {required: false}}}
                                            label={t("students.address.state")}
                                            placeholder={t("students.address.placeholders.state")}
                                            required
                                            error={!!errors.area}
                                            helperText={errors.area?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item size={12}>
                                <div className="divider mt-4 mb-12"/>
                                <Typography variant="h2">{t("students.address.guardianDetailsTitle")}</Typography>
                            </Grid>
                            <Grid item size={{xs: 12, md: 2}}>
                                <FormControl fullWidth error={!!errors.responsibleGuardianTitle}>
                                    <InputLabel required>{t("students.address.guardianTitle")}</InputLabel>
                                    <Controller
                                        name="responsibleGuardianTitle"
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field}>
                                                {TITLES_OPTIONS.map((title) => (
                                                    <MenuItem key={title.value} value={title.value}>
                                                        {title.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.responsibleGuardianTitle?.message ? (
                                        <FormHelperText>{errors.responsibleGuardianTitle.message}</FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item size={{xs: 12, md: 5}}>
                                <Controller
                                    name="responsibleGuardianForename"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            slotProps={{htmlInput: {maxLength: 100, required: false}}}
                                            label={t("students.address.guardianFirstName")}
                                            placeholder={t("students.address.placeholders.guardianFirstName")}
                                            required
                                            error={!!errors.responsibleGuardianForename}
                                            helperText={errors.responsibleGuardianForename?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 5}}>
                                <Controller
                                    name="responsibleGuardianSurname"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            slotProps={{htmlInput: {maxLength: 100, required: false}}}
                                            label={t("students.address.guardianLastName")}
                                            placeholder={t("students.address.placeholders.guardianLastName")}
                                            required
                                            error={!!errors.responsibleGuardianSurname}
                                            helperText={errors.responsibleGuardianSurname?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Controller
                                    name="responsibleGuardianEmail"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            slotProps={{htmlInput: {required: false}}}
                                            type="email"
                                            label={t("students.address.guardianEmail")}
                                            placeholder={t("students.address.placeholders.guardianEmail")}
                                            required
                                            error={!!errors.responsibleGuardianEmail}
                                            helperText={errors.responsibleGuardianEmail?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={{xs: 12, md: 6}}>
                                <Controller
                                    name="responsibleGuardianPhone"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            onChange={(e) => field.onChange(e.target.value.replace(/[^\d+]/g, ""))}
                                            fullWidth
                                            slotProps={{htmlInput: {required: false}}}
                                            label={t("students.address.guardianPhone")}
                                            placeholder={t("students.address.placeholders.guardianPhone")}
                                            required
                                            error={!!errors.responsibleGuardianPhone}
                                            helperText={errors.responsibleGuardianPhone?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item size={12}>
                                <FormControl fullWidth error={!!errors.responsibleGuardianRelationshipId}>
                                    <InputLabel required>{t("students.address.guardianRelation")}</InputLabel>
                                    <Controller
                                        name="responsibleGuardianRelationshipId"
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field} onChange={(event) => field.onChange(event.target.value)}>
                                                <MenuItem value="">
                                                    <em className="select-placeholder">{t("students.address.placeholders.guardianRelation")}</em>
                                                </MenuItem>
                                                {relationshipsOptions.map((item) => (
                                                    <MenuItem key={item.value} value={item.value}>
                                                        {item.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.responsibleGuardianRelationshipId?.message ? (
                                        <FormHelperText>{errors.responsibleGuardianRelationshipId.message}</FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Grid>
                        </>
                    )}
                    <Grid item size={12} className="w-full flex gap-4 mt-4">
                        {(relationValue && showAddressForm) && (
                            <Button
                                variant="outlined"
                                type="button"
                                onClick={handleCancelChangeAddress}
                                disabled={isSaving}
                            >
                                {t("button.cancel")}
                            </Button>
                        )}
                        <Button
                            variant="accent"
                            type="submit"
                            className="ml-auto"
                            loading={isSaving}
                        >
                            {t("students.finalize")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Preloader>
    );
};
